import type { AgentOrbColors, AgentOrbState } from "./orb-math"
import { orbTargetLevels } from "./orb-math"
import { paintOrbTexture } from "./orb-texture"

// One WebGL2 context draws every orb in turn and copies it into each orb's 2D canvas.
// Browsers cap live WebGL contexts at about 16, so a context per avatar breaks in a long timeline.

const VS = `#version 300 es
in vec2 p; out vec2 vUv;
void main(){ vUv = p * 0.5 + 0.5; gl_Position = vec4(p, 0.0, 1.0); }`

// A painted ball seen through a glass sphere: the texture is refracted onto the
// sphere, then pushed around by domain-warped fbm and a slow simplex drift.
// Speaking adds a light ring that sweeps round the rim.
const FS = `#version 300 es
precision highp float;
uniform float uTime; uniform float uWarpTime; uniform float uSeed;
uniform float uInput; uniform float uOutput; uniform float uPx;
uniform sampler2D uTexture;
in vec2 vUv; out vec4 outColor;

vec3 mod289(vec3 x) { return x - floor(x / 289.0) * 289.0; }
vec4 mod289(vec4 x) { return x - floor(x / 289.0) * 289.0; }
vec4 permute(vec4 x) { return mod289(((x * 34.0) + 1.0) * x); }
// Simplex noise by Ashima Arts (MIT).
float simplex(vec3 v) {
  const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
  vec3 i = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);
  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);
  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - 0.5;
  i = mod289(i);
  vec4 p = permute(permute(permute(i.z + vec4(0.0, i1.z, i2.z, 1.0)) + i.y + vec4(0.0, i1.y, i2.y, 1.0)) + i.x + vec4(0.0, i1.x, i2.x, 1.0));
  vec3 ns = 0.142857142857 * vec3(2.0, 0.5, 1.0) - vec3(0.0, 1.0, 0.0);
  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);
  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);
  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);
  vec4 s0 = floor(b0) * 2.0 + 1.0;
  vec4 s1 = floor(b1) * 2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));
  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;
  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);
  vec4 norm = 1.79284291400159 - 0.85373472095314 * vec4(dot(p0, p0), dot(p1, p1), dot(p2, p2), dot(p3, p3));
  p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
  vec4 m = max(0.6 - vec4(dot(x0, x0), dot(x1, x1), dot(x2, x2), dot(x3, x3)), 0.0);
  m = m * m;
  return 42.0 * dot(m * m, vec4(dot(p0, x0), dot(p1, x1), dot(p2, x2), dot(p3, x3)));
}

float hash(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
float valueNoise(vec2 p) {
  vec2 i = floor(p); vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = hash(i), b = hash(i + vec2(1.0, 0.0)), c = hash(i + vec2(0.0, 1.0)), d = hash(i + vec2(1.0, 1.0));
  return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
}
float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  mat2 rot = mat2(0.8776, 0.4794, -0.4794, 0.8776);
  for (int i = 0; i < 4; i++) { v += a * valueNoise(p); p = rot * p * 2.0 + vec2(100.0); a *= 0.5; }
  return v;
}

void main() {
  float radius = 1.0 - pow(uInput * 0.75, 3.0) * 0.1;
  vec2 centered = vUv - 0.5;
  float dist = length(centered) * 2.0 / radius;

  // Refract the picture onto a sphere: flat in the middle, squeezed at the rim.
  vec2 uv = vec2(1.0 - vUv.x, vUv.y);
  vec2 dotUv = (uv - 0.5) * 2.0 / radius;
  float depth = pow(sqrt(1.0 - clamp(dot(dotUv, dotUv), 0.0, 1.0)), 1.1);
  vec2 normal = dotUv;
  dotUv /= (depth + 1.0) / 0.9;
  uv = (dotUv + 1.0) * 0.5;

  // Domain-warped fbm bends the picture along the sphere's surface.
  vec2 f = uv * 3.25 + uSeed;
  vec2 q = vec2(fbm(f), fbm(f + 1.0));
  vec2 r = vec2(fbm(f + q + vec2(91.3, 0.55) + 0.15 * uWarpTime), fbm(f + q - vec2(45.33, 1.2) + 0.126 * uWarpTime));
  float warp = mix(0.8, 0.66, clamp(pow(fbm(f + r), 2.0) * 2.75, 0.0, 1.0));
  warp = mix(warp, 0.0, clamp(length(q), 0.0, 1.0));
  warp = mix(warp, 1.0, clamp(r.x, 0.0, 1.0));

  vec2 drift = vec2(simplex(vec3(vUv * 0.65, uTime * 0.125)), simplex(vec3(vUv * 0.65 + 54.0, uTime * 0.25)));
  drift *= 1.0 + uOutput * 0.25;
  uv += normal * (warp - 0.5) * 0.65 + drift * 0.15;
  vec3 color = texture(uTexture, uv).rgb;
  color = mix(vec3(dot(color, vec3(0.2125, 0.7154, 0.0721))), color, 1.25);
  color = clamp(0.5 + 1.08 * (color - 0.5), 0.0, 1.0);

  // While speaking, a soft light ring sweeps round inside the rim.
  vec2 ringUv = centered * 2.0 / radius * 0.75;
  float ang = atan(ringUv.y, ringUv.x);
  float len = length(ringUv);
  float ringTime = -uTime * 0.5;
  ringUv.x += 1.0 + uOutput * 1.5 - uInput * 2.5;
  float n0 = simplex(vec3(ringUv * (0.65 + uOutput * 0.4), ringTime * 0.5)) * 0.5 + 0.5;
  float sweep = cos(ang + ringTime * 2.0) * 0.5 + 0.5;
  sweep *= smoothstep(1.0, mix(0.25, 1.0, n0 * 0.5), len) * pow(smoothstep(0.25, mix(0.25, 1.0, n0 * 0.75), len), 2.0);
  sweep = clamp(pow(sweep * min(uOutput * 1.6 + uInput * 1.2, 1.0), 3.0), 0.0, 1.0);
  color = clamp(color + sweep * 0.25, 0.0, 1.0);

  float alpha = 1.0 - smoothstep(1.0 - 1.5 * uPx, 1.0, dist);
  outColor = vec4(color * alpha, alpha);
}`

const BUFFER = 256

interface Orb {
	canvas: HTMLCanvasElement
	ctx: CanvasRenderingContext2D
	pixels: number
	state: AgentOrbState
	colorKey: string
	seed: number
	time: number
	warpTime: number
	input: number
	output: number
	visible: boolean
	dirty: boolean
	onFirstDraw?: () => void
}

interface Renderer {
	gl: WebGL2RenderingContext
	glCanvas: HTMLCanvasElement
	uniforms: Record<string, WebGLUniformLocation | null>
	textures: Map<string, WebGLTexture>
}

let renderer: Renderer | null | undefined
const orbs = new Set<Orb>()
let observer: IntersectionObserver | null = null
let frameId: number | null = null
let frameCount = 0
let lastTime = 0

function createRenderer(): Renderer | null {
	if (typeof document === "undefined") return null
	const glCanvas = document.createElement("canvas")
	glCanvas.width = glCanvas.height = BUFFER
	let gl: WebGL2RenderingContext | null = null
	try {
		gl = glCanvas.getContext("webgl2", {
			alpha: true,
			premultipliedAlpha: true,
			preserveDrawingBuffer: true,
			antialias: false,
		})
	} catch {
		return null
	}
	if (!gl) return null
	const compile = (type: number, src: string) => {
		const shader = gl.createShader(type)!
		gl.shaderSource(shader, src)
		gl.compileShader(shader)
		return shader
	}
	const program = gl.createProgram()!
	gl.attachShader(program, compile(gl.VERTEX_SHADER, VS))
	gl.attachShader(program, compile(gl.FRAGMENT_SHADER, FS))
	gl.linkProgram(program)
	if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null
	gl.useProgram(program)
	gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer())
	gl.bufferData(
		gl.ARRAY_BUFFER,
		new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
		gl.STATIC_DRAW,
	)
	const position = gl.getAttribLocation(program, "p")
	gl.enableVertexAttribArray(position)
	gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0)
	gl.clearColor(0, 0, 0, 0)
	const uniforms: Renderer["uniforms"] = {}
	for (const name of [
		"uTime",
		"uWarpTime",
		"uSeed",
		"uInput",
		"uOutput",
		"uPx",
		"uTexture",
	])
		uniforms[name] = gl.getUniformLocation(program, name)
	gl.uniform1i(uniforms.uTexture!, 0)
	glCanvas.addEventListener("webglcontextlost", () => {
		renderer = null
	})
	return { gl, glCanvas, uniforms, textures: new Map() }
}

function getRenderer() {
	if (renderer === undefined) renderer = createRenderer()
	return renderer
}

const colorKeyOf = (colors: AgentOrbColors) => colors.join(",")

function textureFor({ gl, textures }: Renderer, key: string) {
	const cached = textures.get(key)
	if (cached) return cached
	const picture = document.createElement("canvas")
	paintOrbTexture(picture, key.split(",") as unknown as AgentOrbColors)
	const texture = gl.createTexture()!
	gl.bindTexture(gl.TEXTURE_2D, texture)
	gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true)
	gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, picture)
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.MIRRORED_REPEAT)
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.MIRRORED_REPEAT)
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
	textures.set(key, texture)
	return texture
}

const prefersReducedMotion = () =>
	typeof matchMedia !== "undefined" &&
	matchMedia("(prefers-reduced-motion: reduce)").matches

function step(orb: Orb, dt: number) {
	orb.time += dt
	const { input, output } = orbTargetLevels(orb.state, orb.time)
	orb.input += (input - orb.input) * 0.2
	orb.output += (output - orb.output) * 0.2
	// The warp runs faster while the agent works, and settles when it rests.
	orb.warpTime += dt * (1.2 + orb.output * 2.4)
}

function draw(r: Renderer, orb: Orb) {
	const { gl, glCanvas, uniforms } = r
	const size = orb.pixels
	gl.viewport(0, 0, size, size)
	gl.clear(gl.COLOR_BUFFER_BIT)
	gl.activeTexture(gl.TEXTURE0)
	gl.bindTexture(gl.TEXTURE_2D, textureFor(r, orb.colorKey))
	gl.uniform1f(uniforms.uTime!, orb.time)
	gl.uniform1f(uniforms.uWarpTime!, orb.warpTime)
	gl.uniform1f(uniforms.uSeed!, orb.seed)
	gl.uniform1f(uniforms.uInput!, orb.input)
	gl.uniform1f(uniforms.uOutput!, orb.output)
	gl.uniform1f(uniforms.uPx!, 2 / size)
	gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
	orb.ctx.clearRect(0, 0, size, size)
	orb.ctx.drawImage(glCanvas, 0, BUFFER - size, size, size, 0, 0, size, size)
	orb.dirty = false
	orb.onFirstDraw?.()
	orb.onFirstDraw = undefined
}

/** Idle orbs drift at a third of the frame rate; an active orb draws every frame. */
export function shouldDrawOrb(
	orb: Pick<Orb, "visible" | "dirty" | "state">,
	frame: number,
	reducedMotion: boolean,
) {
	if (!orb.visible) return false
	if (orb.dirty) return true
	if (reducedMotion) return false
	return orb.state !== "idle" || frame % 3 === 0
}

function tick(now: number) {
	frameId = null
	const r = getRenderer()
	if (!r || orbs.size === 0) return
	const dt = lastTime ? Math.min(0.1, (now - lastTime) / 1000) : 0
	lastTime = now
	frameCount++
	const reduced = prefersReducedMotion()
	for (const orb of orbs) {
		if (!shouldDrawOrb(orb, frameCount, reduced)) continue
		// Idle orbs skip two frames out of three, so they advance three frames' worth of time.
		step(orb, reduced ? 0 : orb.state === "idle" ? dt * 3 : dt)
		draw(r, orb)
	}
	frameId = requestAnimationFrame(tick)
}

function ensureLoop() {
	if (frameId === null && typeof requestAnimationFrame !== "undefined") {
		lastTime = 0
		frameId = requestAnimationFrame(tick)
	}
}

export interface OrbHandle {
	update: (next: { state: AgentOrbState; colors: AgentOrbColors }) => void
	dispose: () => void
}

/** Twice the screen's pixels, so a zoomed-in orb stays sharp; small avatars cost little. */
export const orbPixelSize = (size: number, dpr: number) =>
	Math.max(8, Math.min(BUFFER, Math.round(size * Math.max(1, dpr) * 2)))

/** Returns null without WebGL2; the caller keeps its CSS gradient then. */
export function registerOrb(
	canvas: HTMLCanvasElement,
	options: {
		size: number
		state: AgentOrbState
		colors: AgentOrbColors
		seed: number
		onFirstDraw?: () => void
	},
): OrbHandle | null {
	if (!getRenderer()) return null
	const ctx = canvas.getContext("2d")
	if (!ctx) return null
	const dpr = typeof devicePixelRatio === "number" ? devicePixelRatio : 1
	const pixels = orbPixelSize(options.size, dpr)
	canvas.width = canvas.height = pixels
	const orb: Orb = {
		canvas,
		ctx,
		pixels,
		state: options.state,
		colorKey: colorKeyOf(options.colors),
		seed: options.seed,
		time: Math.random() * 40,
		warpTime: Math.random() * 40,
		input: 0,
		output: 0.3,
		visible: false,
		dirty: true,
		onFirstDraw: options.onFirstDraw,
	}
	orbs.add(orb)
	if (typeof IntersectionObserver !== "undefined") {
		observer ??= new IntersectionObserver((entries) => {
			for (const entry of entries)
				for (const o of orbs)
					if (o.canvas === entry.target) o.visible = entry.isIntersecting
			ensureLoop()
		})
		observer.observe(canvas)
	} else {
		orb.visible = true
	}
	ensureLoop()
	return {
		update({ state, colors }) {
			orb.state = state
			orb.colorKey = colorKeyOf(colors)
			orb.dirty = true
			ensureLoop()
		},
		dispose() {
			orbs.delete(orb)
			observer?.unobserve(canvas)
		},
	}
}
